import { useEffect, useMemo, useRef, useState } from 'react';
import { useSites } from '../../../hooks/useSites';
import { useSetSiteOrgUnit } from '../../../hooks/useOrgUnits';

interface Props {
  orgUnitId: string;
  onClose: () => void;
}

export function OrgUnitSiteBindingModal({ orgUnitId, onClose }: Props) {
  const sitesQuery = useSites({ limit: 200 });
  const setBinding = useSetSiteOrgUnit();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initialSelected, setInitialSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Hydrate once per orgUnitId. Re-hydrating on every sitesQuery.data ref change
  // would wipe user selections during background refetches (including the cache
  // invalidation that fires mid-save after each successful binding mutation).
  const lastInitKey = useRef<string | null>(null);

  useEffect(() => {
    if (sitesQuery.data && lastInitKey.current !== orgUnitId) {
      const initial = new Set(
        sitesQuery.data.sites
          .filter((s: any) => s.org_unit_id === orgUnitId)
          .map((s: any) => s.site_id),
      );
      setSelected(initial);
      setInitialSelected(initial);
      lastInitKey.current = orgUnitId;
    }
  }, [sitesQuery.data, orgUnitId]);

  const diff = useMemo(() => {
    const added: string[] = [];
    const removed: string[] = [];
    for (const id of selected) if (!initialSelected.has(id)) added.push(id);
    for (const id of initialSelected) if (!selected.has(id)) removed.push(id);
    return { added, removed };
  }, [selected, initialSelected]);

  function toggle(siteId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  }

  async function handleSave() {
    setPending(true);
    setError(null);
    const tasks = [
      ...diff.added.map((id) => setBinding.mutateAsync({ siteId: id, orgUnitId })),
      ...diff.removed.map((id) => setBinding.mutateAsync({ siteId: id, orgUnitId: null })),
    ];
    const results = await Promise.allSettled(tasks);
    const failures = results.filter((r) => r.status === 'rejected');
    setPending(false);
    if (failures.length > 0) {
      setError(`${failures.length} binding${failures.length === 1 ? '' : 's'} failed`);
    } else {
      onClose();
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      <div className="bg-ems-bg-card border border-ems-border rounded-lg w-full max-w-md p-4">
        <div className="flex justify-between items-center mb-3">
          <div className="font-semibold text-ems-text-primary text-sm">Bind sites</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-ems-text-muted"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-2 px-3 py-2 rounded text-xs bg-ems-red/15 text-ems-red">
            {error}
          </div>
        )}

        <div className="max-h-72 overflow-y-auto border border-ems-border rounded divide-y divide-ems-border mb-3">
          {(sitesQuery.data?.sites ?? []).map((s: any) => (
            <label
              key={s.site_id}
              className="flex items-center gap-2 p-2 cursor-pointer hover:bg-ems-forest/40"
            >
              <input
                type="checkbox"
                checked={selected.has(s.site_id)}
                onChange={() => toggle(s.site_id)}
              />
              <span className="text-sm text-ems-text-primary">{s.name}</span>
              {s.org_unit_id && s.org_unit_id !== orgUnitId && (
                <span className="ml-auto text-[10px] text-ems-text-muted">bound elsewhere</span>
              )}
            </label>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded text-xs border border-ems-border text-ems-text-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={pending || (diff.added.length === 0 && diff.removed.length === 0)}
            className="px-3 py-1.5 rounded text-xs bg-ems-forest text-ems-green border border-ems-green/20 disabled:opacity-40"
          >
            {pending ? 'Saving…' : 'Save binding'}
          </button>
        </div>
      </div>
    </div>
  );
}
